import { useEffect, useState, useMemo } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { connect } from "react-redux";
import Select from "../../../../components/AppSelect";
import { useHistory } from "react-router";
import { Button } from "react-bootstrap";
import Swal from 'sweetalert2';

import Sidebar from "../../../../components/Navigation/Sidebar";
import Topbar from "../../../../components/Navigation/Topbar";
import Header from "../../../../components/Page/header";
import CollapsedSection from "../../../../components/CollapsedSection";
import SubmitButton from "../../../../components/Common/submitButton";
import AddDetailRow from "../../../../components/Modal/WMS/Transfers/addDetailRow";
import Grid from '../../../../components/Grid';

import { customStyles, largerSelectStyles } from '../../../../constants/customSelectStyles';

import { buttonIcon, buttonTypes } from "../../../../utils/buttonsUtil";
import { hasNoCountryLimitations } from "../../../../utils/functionsUtils";

const billingResponsibilityOptions = [
    { value: 'KIKI', label: 'Kiki Latam' },
    { value: 'SHIPPER', label: 'Remitente' },
];

const WMSOutboundTransfer = ({
    reduxGetHubs,
    allBranches,
    reduxGetLogisticsOperators,
    logisticsOperators,
    reduxGetHubInventory,
    hubInventory,
    reduxResetHubInventory,
    reduxCreateTransfer,
    requestingCreateTransfer,
    successfulCreateTransfer,
    errorCreateTransfer,
    transferResult,
    reduxResetCreateTransfer,
    reduxDownloadManifest,
}) => {
    const history = useHistory();

    const userData = useMemo(() => JSON.parse(window.localStorage.getItem('userData')), []);
    const countryUserStr = userData?.idCountry?.toString() || null;
    const viewAllCountriesData = hasNoCountryLimitations();
    const hasInventoryAdminPermission = userData.role.permissionsRoles.some(
        permissionRole => permissionRole.permission.name === 'feature:wms-inventory-admin'
    );

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            hubOrigin: null,
            hubDestination: null,
            billing_responsibility: billingResponsibilityOptions[0],
            operator: null,
            tracking_number: '',
            transferDetails: [],
            files: [],
            observations: '',
        }
    });

    const { fields, append, remove } = useFieldArray({
        register,
        control,
        name: "transferDetails",
    });

    const hubOriginWatcher = watch('hubOrigin', null);
    const hubDestinationWatcher = watch('hubDestination', null);

    const [showMainData, setShowMainData] = useState(true);
    const [showTransferDetails, setShowTransferDetails] = useState(true);
    const [showEvidences, setShowEvidences] = useState(true);

    const [disableOriginSelect, setDisableOriginSelect] = useState(false);
    const [showTransferShipData, setShowTransferShipData] = useState(false);
    const [showAddDetailModal, setShowAddDetailModal] = useState(false);

    useEffect(() => {
        reduxGetHubs({
            isActive: true,
        });

        reduxGetLogisticsOperators();
    }, []);

    useEffect(() => {
        if (!allBranches) return;

        if (!hasInventoryAdminPermission) {
            if (!userData.idHub) {
                Swal.fire(
                    "No permitido",
                    `Lo sentimos pero su usuario no está asociado a ningún Hub`,
                    "error"
                );
                history.goBack();
            } else {
                setValue('hubOrigin', {
                    value: userData?.hub?.idBranch,
                    label: userData?.hub?.branchName,
                    idWarehouse: userData?.hub?.hubWarehouses[0]?.idWarehouse,
                });
                setDisableOriginSelect(true);
            }
        }
    }, [allBranches]);

    useEffect(() => {
        if (hubOriginWatcher?.value !== hubDestinationWatcher?.value) {
            setShowTransferShipData(true);
        } else {
            setValue('operator', null);
            setValue('tracking_number', '');
            setShowTransferShipData(false);
        }
    }, [hubOriginWatcher, hubDestinationWatcher]);

    useEffect(() => {
        remove();
        if (!hubOriginWatcher) {
            reduxResetHubInventory();
            return;
        };

        reduxGetHubInventory({
            hub: hubOriginWatcher?.value,
        });
    }, [hubOriginWatcher, remove]);

    useEffect(() => {
        if (successfulCreateTransfer) {
            Swal.fire({
                icon: 'success',
                title: 'Proceso exitoso',
                text: transferResult.message,
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    const idManifest = transferResult.data.idOutboundManifest;
                    if (idManifest) {
                        reduxDownloadManifest({
                            idManifest
                        })
                    }
                    reduxResetCreateTransfer();
                    history.goBack();
                }
            });
        }

        if (errorCreateTransfer) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: transferResult.message,
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
        }
    }, [successfulCreateTransfer, errorCreateTransfer, transferResult]);

    const handleChangeFiles = (e) => {
        setValue('files', Array.from(e.target.files))
    }

    const handleDeleteRow = (index) => {
        remove(index);
    };

    const handleAddDetail = (detail) => {
        const newDetail = {
            shipper: detail.shipper,
            product: detail.product,
            originSubwarehouseType: detail.originSubwarehouseType,
            quantity: detail.quantity,
        };
        append(newDetail);
    };

    const handleSave = (data) => {
        if (!data.transferDetails || data.transferDetails.length === 0) {
            Swal.fire(
                'Error',
                'Debe agregar al menos un producto al detalle de traslado',
                'error'
            );
            return;
        }

        Swal.fire({
            icon: 'warning',
            title: '¿Estás seguro?',
            text: '¿Estás seguro de crear el traslado?, los productos no podrán ser modificados o eliminados una vez creado.',
            confirmButtonText: 'Sí',
            cancelButtonText: 'No',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
        }).then((result) => {
            if (result.isConfirmed) {
                const payload = {
                    idOriginHub: data.hubOrigin.value,
                    idDestinationHub: data.hubDestination.value,
                    billingResponsibility: data.billing_responsibility.value,
                    idOperator: data.operator?.value,
                    trackingNumber: data.tracking_number,
                    lineItems: data.transferDetails.map(row => ({
                        idProduct: row.product.value,
                        idOriginWarehouse: data.hubOrigin.idWarehouse,
                        originSubwarehouseType: row.originSubwarehouseType?.value,
                        quantity: Number(row.quantity)
                    })),
                    // files: data.files ? data.files : [],
                    comments: data.observations,
                };

                reduxCreateTransfer(payload);
            }
        });
    };

    const transferDetailsColumns = [
        {
            title: "Remitente",
            render: (item) => <span>{item.shipper?.label}</span>,
        },
        {
            title: "Producto",
            render: (item) => <span>{item.product?.label}</span>,
        },
        {
            title: "Cantidad",
            render: (item) => <span>{item.quantity}</span>,
        },
        {
            title: "Bodega de Origen",
            render: (item) => <span>{item.originSubwarehouseType?.label}</span>,
        },
        {
            title: "Acciones",
            render: (item) => (
                <button
                    type="button"
                    onClick={() => handleDeleteRow(item.index)}
                >
                    <i className="fa fa-trash"></i>
                </button>
            ),
        },
    ];

    const actionButtons = [
        {
            onClick: () => history.goBack(),
            buttonType: buttonTypes.Primary,
            buttonIcon: buttonIcon.Arrow_LeftReply,
        },
    ];

    return (
        <>
            <div id="wrapper">
                <Sidebar />
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        <Topbar />
                        <div className="container-fluid">
                            <Header title={"Nuevo traslado"} actionButtons={actionButtons} />
                        </div>
                        <form onSubmit={handleSubmit(handleSave)}>
                            <div className="container-fluid card card-body">
                                <div className={"d-flex flex-column gap-1"}>
                                    <CollapsedSection show={showMainData} title="Datos principales del traslado" sectionKey="mainData" onToggle={() => setShowMainData(!showMainData)}>
                                        <div className="row form-group">
                                            <div className="col-4">
                                                <label htmlFor="hubOrigin">Hub de Origen *</label>
                                                <Controller
                                                    control={control}
                                                    name="hubOrigin"
                                                    rules={{ required: 'Este campo es requerido' }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Select
                                                                {...field}
                                                                styles={largerSelectStyles}
                                                                isClearable={true}
                                                                isDisabled={disableOriginSelect}
                                                                options={
                                                                    allBranches?.length > 0 ?
                                                                        allBranches
                                                                            .filter(e =>
                                                                                hasInventoryAdminPermission ||
                                                                                (viewAllCountriesData || e.idCountry === countryUserStr))
                                                                            .map(ele => ({
                                                                                value: ele.idBranch,
                                                                                label: ele.branchName,
                                                                                idWarehouse: ele.hubWarehouses[0]?.idWarehouse ?? null,
                                                                            }))
                                                                        : []
                                                                }
                                                            />
                                                            {errors.hubOrigin && <span className="error-message">{errors.hubOrigin.message}</span>}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className="col-4">
                                                <label htmlFor="hubDestination">Hub de Destino *</label>
                                                <Controller
                                                    control={control}
                                                    name="hubDestination"
                                                    rules={{ required: 'Este campo es requerido' }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Select
                                                                {...field}
                                                                styles={largerSelectStyles}
                                                                isClearable={true}
                                                                options={
                                                                    allBranches?.length > 0 ? allBranches
                                                                        .filter(e =>
                                                                            hasInventoryAdminPermission ||
                                                                            (viewAllCountriesData || e.idCountry === Number(countryUserStr))
                                                                        )
                                                                        .map(ele => ({
                                                                            value: ele.idBranch,
                                                                            label: ele.branchName,
                                                                            idWarehouse: ele.hubWarehouses[0]?.idWarehouse ?? null,
                                                                        }))
                                                                        : []
                                                                }
                                                            />
                                                            {errors.hubDestination && <span className="error-message">{errors.hubDestination.message}</span>}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className="col-auto">
                                                <label htmlFor="billing_responsibility">Originado por *</label>
                                                <Controller
                                                    control={control}
                                                    name="billing_responsibility"
                                                    rules={{ required: 'Este campo es requerido' }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Select
                                                                {...field}
                                                                styles={customStyles}
                                                                options={[
                                                                    { value: 'KIKI', label: 'Kiki Latam' },
                                                                    { value: 'SHIPPER', label: 'Remitente' }
                                                                ]}
                                                            />
                                                            {errors.billing_responsibility && <span className="error-message">{errors.billing_responsibility.message}</span>}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {showTransferShipData && (
                                            <div className="row form-group">
                                                <div className="col-4">
                                                    <label htmlFor="operator">Operador *</label>
                                                    <Controller
                                                        control={control}
                                                        name="operator"
                                                        rules={
                                                            {
                                                                validate: (value) => {
                                                                    if (showTransferShipData && !value) { return 'Este campo es requerido'; }
                                                                    else { return true; }
                                                                }
                                                            }
                                                        }
                                                        isClearable={true}
                                                        render={({ field }) => (
                                                            <>
                                                                <Select
                                                                    {...field}
                                                                    styles={customStyles}
                                                                    isClearable={true}
                                                                    onChange={(e) => {
                                                                        field.onChange(e);
                                                                        setValue('tracking_number', null);
                                                                    }}
                                                                    options={
                                                                        logisticsOperators?.items?.length > 0 ? logisticsOperators.items
                                                                            .filter(ele => ele.isActive)
                                                                            .map(operator => ({
                                                                                value: operator.idCompany,
                                                                                label: operator.businessName,
                                                                            })) : []
                                                                    }
                                                                />
                                                                {errors.operator && <span className="error-message">{errors.operator.message}</span>}
                                                            </>
                                                        )}
                                                    />
                                                </div>
                                                <div className="col-auto">
                                                    <label htmlFor="tracking_number">Guía *</label>
                                                    <input
                                                        {...register('tracking_number', {
                                                            validate: (value) => {
                                                                if (showTransferShipData && !value) { return 'Este campo es requerido'; }
                                                                else { return true; }
                                                            }
                                                        })}
                                                        type="text"
                                                        className="form-control"
                                                    />
                                                    {errors.tracking_number && <span className="error-message">{errors.tracking_number.message}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </CollapsedSection>

                                    <CollapsedSection show={showTransferDetails} title="Detalle de traslado" sectionKey="detailData" onToggle={() => setShowTransferDetails(!showTransferDetails)}>
                                        <div className="d-flex">
                                            <Button variant="primary" type="button" disabled={Object.keys(hubInventory).length === 0} onClick={() => setShowAddDetailModal(true)}>
                                                Agregar Detalle
                                            </Button>
                                        </div>
                                        <Grid
                                            showFilters={false}
                                            showRange={false}
                                            cols={transferDetailsColumns}
                                            data={fields.map((row, index) => ({ ...row, index }))}
                                        />
                                        <AddDetailRow
                                            show={showAddDetailModal}
                                            handleClose={() => setShowAddDetailModal(false)}
                                            handleAdd={handleAddDetail}
                                            hubInventory={hubInventory}
                                            hasInventoryAdminPermission={hasInventoryAdminPermission}
                                            viewAllCountriesData={viewAllCountriesData}
                                            countryUserStr={countryUserStr}
                                            fields={fields}
                                        />
                                    </CollapsedSection>

                                    <CollapsedSection show={showEvidences} title="Evidencias" sectionKey="evidences" onToggle={() => setShowEvidences(!showEvidences)}>
                                        {/* <div className="form-group mt-3">
                                        <Controller
                                            control={control}
                                            name="files"
                                            render={() => (
                                                <>
                                                    <label
                                                        htmlFor='files'
                                                        className='form-label'>
                                                        Evidencias
                                                    </label>
                                                    <MultiFile
                                                        files={filesWatcher}
                                                        accept="image/*"
                                                        onChange={handleChangeFiles}
                                                    />
                                                    {
                                                        errors.files &&
                                                        <span className="error-message">
                                                            {errors.files.message}
                                                        </span>
                                                    }
                                                </>
                                            )}
                                        />
                                    </div> */}
                                        <div className="form-group mt-1">
                                            <label htmlFor="observations">Comentarios/Observaciones</label>
                                            <textarea
                                                className="form-control"
                                                id="observations"
                                                rows="3"
                                                {...register('observations')}
                                            ></textarea>
                                        </div>
                                    </CollapsedSection>
                                    <div className='d-flex justify-content-center mt-3'>
                                        <SubmitButton
                                            type='submit'
                                            variant='primary'
                                            text='Crear traslado'
                                            loading={requestingCreateTransfer}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        allBranches: state.branchState.allBranches,
        logisticsOperators: state.companyState.logisticsOperators,
        hubInventory: state.inventoryState.hubInventory,
        requestingCreateTransfer: state.wmsTransfersState.requestingCreateTransfer,
        errorCreateTransfer: state.wmsTransfersState.errorCreateTransfer,
        successfulCreateTransfer: state.wmsTransfersState.successfulCreateTransfer,
        transferResult: state.wmsTransfersState.transferResult,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        reduxGetHubs: (payload) =>
            dispatch({
                type: "FETCH_ALL_BRANCHES_REQUEST",
                value: payload,
            }),
        reduxGetLogisticsOperators: (payload) =>
            dispatch({
                type: "FETCH_COMPANIESBYLOGISTICSOPERATOR_REQUEST",
                value: payload,
            }),
        reduxGetHubInventory: (payload) =>
            dispatch({
                type: "FETCH_HUB_INVENTORY_REQUEST",
                value: payload,
            }),
        reduxResetHubInventory: () =>
            dispatch({
                type: "RESET_HUB_INVENTORY",
            }),
        reduxCreateTransfer: (payload) =>
            dispatch({
                type: "CREATE_NEW_TRANSFER_REQUEST",
                value: payload,
            }),
        reduxResetCreateTransfer: () =>
            dispatch({
                type: "CREATE_NEW_TRANSFER_RESET",
            }),
        reduxDownloadManifest: (payload) =>
            dispatch({
                type: "DOWNLOAD_MANIFEST_REQUEST",
                value: payload,
            }),
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(WMSOutboundTransfer);